import React, { useState, useEffect } from 'react';
import DatePicker from './DatePicker';
import ConfirmModal from './ConfirmModal';
import { useLocation } from 'react-router-dom';
import './DailyLogger.css';
import LoadingState from './LoadingState';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function DailyLogger({ user, managedOwner }) {
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allDealershipEmployees, setAllDealershipEmployees] = useState([]);
  const [namedFitSpecialists, setNamedFitSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    entryId: null,
    onConfirm: null,
    title: '',
    message: '',
    confirmText: 'Delete',
    confirmStyle: 'danger'
  });
  const [selectedDealership, setSelectedDealership] = useState('');
  const [viewingAllDealerships, setViewingAllDealerships] = useState(false);
  
  // Filter states
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sort states
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Duplicate entry modal states
  const [duplicateModal, setDuplicateModal] = useState({
    show: false,
    entry: null,
    resolve: null
  });
  
  // Bulk import wizard states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload'); // upload | review | importing | done
  const [bulkImportError, setBulkImportError] = useState(null);
  const [bulkImportSuccess, setBulkImportSuccess] = useState(null);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkCreateMissing, setBulkCreateMissing] = useState(true);
  const [bulkFileName, setBulkFileName] = useState('');
  
  // Helper to get local date in YYYY-MM-DD format
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    date: getLocalDateString(),
    dealershipId: '',
    employeeId: '',
    greets: 0,
    sitDowns: 0,
    testRides: 0,
    writeUps: 0
  });

  const isOwner = user?.role === 'OWNER' || managedOwner;
  const isManager = user?.role === 'MANAGER';
  // Base this on the dealerships actually loaded into the form (which, for an
  // admin/site-manager managing an owner, are the managed owner's dealerships),
  // falling back to the logged-in user's own dealerships.
  const hasMultipleDealerships = dealerships.length > 1 || (Array.isArray(user?.dealerships) && user.dealerships.length > 1);
  const singleDealership = user?.dealershipId || (user?.dealerships && user.dealerships.length === 1 ? user.dealerships[0].id : null);
  
  const fitSpecialists = employees.filter(e => Array.isArray(e.roles) && e.roles.includes('Fit Specialist'));
  const formFitSpecialists = (isOwner && viewingAllDealerships && formData.dealershipId)
    ? fitSpecialists.filter(e => e.dealershipId === formData.dealershipId)
    : fitSpecialists;
  const isCurrentFitSpecialist = Array.isArray(user?.roles) && user.roles.includes('Fit Specialist');

  useEffect(() => {
    if (isOwner) {
      fetchDealerships();
    } else if (singleDealership) {
      setSelectedDealership(singleDealership);
      setFormData(prev => ({ ...prev, dealershipId: singleDealership }));
    }
  }, [isOwner, singleDealership, managedOwner]);

  useEffect(() => {
    const activeDealership = selectedDealership || singleDealership;
    if (activeDealership && activeDealership !== 'ALL') {
      fetchEntries(activeDealership);
      fetchEmployees(activeDealership);
      fetchNamedFitSpecialists(activeDealership);
    } else if (activeDealership === 'ALL') {
      fetchEntries('ALL');
    }
  }, [selectedDealership, singleDealership]);

  useEffect(() => {
    if (isCurrentFitSpecialist && user?.id) {
      setFormData(prev => ({ ...prev, employeeId: user.id }));
    } else if (!isCurrentFitSpecialist && !formData.employeeId) {
      if (formFitSpecialists.length > 0) {
        setFormData(prev => ({ ...prev, employeeId: formFitSpecialists[0].id }));
      } else if (namedFitSpecialists.length > 0) {
        setFormData(prev => ({ ...prev, employeeId: `named:${namedFitSpecialists[0].value}` }));
      }
    }
  }, [isCurrentFitSpecialist, formFitSpecialists, namedFitSpecialists, formData.employeeId, user?.id]);

  // Check if navigating from Overview alert to open new entry form
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === 'true') {
      setShowForm(true);
    }
  }, [location.search]);

  const fetchDealerships = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      let url = `${API_URL}/api/owners/dealerships`;
      if (managedOwner) {
        url += `?ownerId=${managedOwner.id}`;
      }

      const res = await fetch(url, {
        headers,
        credentials: 'include'
      });

      if (!res.ok) return;
      const data = await res.json();
      // Deduplicate dealerships by ID
      const deduplicatedDealerships = Array.from(new Map((data.dealerships || []).map(d => [d.id, d])).values());
      setDealerships(deduplicatedDealerships);
      
      // Auto-select first dealership for form and set viewing all for display
      if (deduplicatedDealerships.length > 0) {
        const firstDealershipId = deduplicatedDealerships[0].id;
        setFormData(prev => ({ ...prev, dealershipId: firstDealershipId }));

        // For owners with multiple dealerships, default to viewing all
        if (deduplicatedDealerships.length > 1) {
          setSelectedDealership('ALL');
          setViewingAllDealerships(true);
          // Fetch employees for all dealerships so filter panel shows Fit Specialists
          const empPromises = deduplicatedDealerships.map(async (d) => {
            const empRes = await fetch(`${API_URL}/api/dealerships/${d.id}/employees`, {
              headers,
              credentials: 'include'
            });
            if (empRes.ok) {
              const empData = await empRes.json();
              return (empData.employees || []).map(e => ({ ...e, dealershipId: d.id }));
            }
            return [];
          });
          const allEmps = (await Promise.all(empPromises)).flat();
          setAllDealershipEmployees(allEmps);
          setEmployees(allEmps);
          fetchNamedFitSpecialists(firstDealershipId);
        } else {
          setSelectedDealership(firstDealershipId);
        }
      }
      
      // Set loading to false so UI shows even if no dealerships
      setLoading(false);
    } catch (err) {
      console.error('Failed to load dealerships', err);
      setLoading(false);
    }
  };

  const fetchEmployees = async (dealershipId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/dealerships/${dealershipId}/employees`, {
        headers,
        credentials: 'include'
      });

      if (!res.ok) return;
      const data = await res.json();
      setEmployees((data.employees || []).map(e => ({ ...e, dealershipId })));
    } catch (err) {
      console.error('Failed to load employees', err);
    }
  };

  const fetchNamedFitSpecialists = async (dealershipId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/FIT_SPECIALIST`, {
        headers,
        credentials: 'include'
      });

      if (!res.ok) { setNamedFitSpecialists([]); return; }
      const data = await res.json();
      setNamedFitSpecialists((data.options || []).map(opt => ({ value: opt.value, label: opt.label })));
    } catch (err) {
      setNamedFitSpecialists([]);
    }
  };

  const fetchEntries = async (dealershipId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Handle viewing all dealerships for owners
      if (dealershipId === 'ALL' && isOwner) {
        // Fetch entries from all dealerships and combine them
        const allEntries = [];
        for (const dealership of dealerships) {
          const response = await fetch(
            `${API_URL}/api/dealerships/${dealership.id}/daily-logs`,
            { credentials: 'include', headers }
          );
          if (response.ok) {
            const data = await response.json();
            allEntries.push(...(data.entries || []));
          }
        }
        // Sort by date descending
        allEntries.sort((a, b) => new Date(b.date) - new Date(a.date));
        setEntries(allEntries);
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/daily-logs`,
        {
          credentials: 'include',
          headers
        }
      );

      if (!response.ok) {
        let message = 'Failed to fetch entries';
        try {
          const err = await response.json();
          if (err?.error) message = err.error;
        } catch (parseErr) {
          // ignore parse errors and keep default message
        }
        throw new Error(message);
      }

      const data = await response.json();
      setEntries(data.entries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Returns the POST body fields for the currently-selected specialist
  const buildSpecialistBody = (employeeId) => {
    if (employeeId?.startsWith('named:')) {
      const namedValue = employeeId.slice(6);
      const namedLabel = namedFitSpecialists.find(ns => ns.value === namedValue)?.label || namedValue;
      return { namedSpecialistValue: namedValue, namedSpecialistName: namedLabel };
    }
    return { targetUserId: employeeId };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.dealershipId) {
        setAlertModal({ isOpen: true, title: 'Missing Information', message: 'Please select a dealership' });
        return;
      }
      if (!formData.employeeId) {
        setAlertModal({ isOpen: true, title: 'Missing Information', message: 'Please select a fit specialist' });
        return;
      }
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const dateString = formData.date;
      const isNamed = formData.employeeId.startsWith('named:');
      const namedValue = isNamed ? formData.employeeId.slice(6) : null;

      if (!editingId) {
        const existingEntry = entries.find(entry => {
          if (entry.date.split('T')[0] !== dateString) return false;
          if (entry.dealershipId !== formData.dealershipId) return false;
          if (isNamed) return !entry.userId && entry.specialistValue === namedValue;
          return entry.userId === formData.employeeId;
        });
        if (existingEntry) {
          await new Promise((resolve) => {
            setDuplicateModal({
              show: true,
              entry: existingEntry,
              resolve
            });
          });
          return;
        }
      }

      // If editing and specialist changed, delete old entry and create new one
      if (editingId) {
        const originalEntry = entries.find(e => e.id === editingId);
        const specialistChanged = originalEntry && (
          (originalEntry.userId || null) !== (isNamed ? null : formData.employeeId) ||
          (originalEntry.specialistValue || null) !== (isNamed ? namedValue : null)
        );
        if (specialistChanged) {
          // Delete old entry
          const deleteResponse = await fetch(`${API_URL}/api/daily-logs/${editingId}`, {
            method: 'DELETE',
            headers,
            credentials: 'include'
          });
          if (!deleteResponse.ok) {
            const error = await deleteResponse.json();
            throw new Error(error.error || 'Failed to update entry (delete old)');
          }
          // Create new entry for new specialist
          const createResponse = await fetch(`${API_URL}/api/dealerships/${formData.dealershipId}/daily-logs`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              date: dateString,
              ...buildSpecialistBody(formData.employeeId),
              greets: parseInt(formData.greets) || 0,
              sitDowns: parseInt(formData.sitDowns) || 0,
              testRides: parseInt(formData.testRides) || 0,
              writeUps: parseInt(formData.writeUps) || 0
            })
          });
          if (!createResponse.ok) {
            const error = await createResponse.json();
            throw new Error(error.error || 'Failed to update entry (create new)');
          }
        } else {
          // Same specialist — normal update
          const url = `${API_URL}/api/daily-logs/${editingId}`;
          const response = await fetch(url, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              date: dateString,
              greets: parseInt(formData.greets) || 0,
              sitDowns: parseInt(formData.sitDowns) || 0,
              testRides: parseInt(formData.testRides) || 0,
              writeUps: parseInt(formData.writeUps) || 0
            })
          });
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to update entry');
          }
        }
      } else {
        // Not editing, just create
        const url = `${API_URL}/api/dealerships/${formData.dealershipId}/daily-logs`;
        const response = await fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            date: dateString,
            ...buildSpecialistBody(formData.employeeId),
            greets: parseInt(formData.greets) || 0,
            sitDowns: parseInt(formData.sitDowns) || 0,
            testRides: parseInt(formData.testRides) || 0,
            writeUps: parseInt(formData.writeUps) || 0
          })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to save entry');
        }
      }
      // Always fetch entries for the dealership of the saved entry
      await fetchEntries(formData.dealershipId);
      setShowForm(false);
      setEditingId(null);
      setFormData({
        date: getLocalDateString(),
        dealershipId: formData.dealershipId,
        employeeId: isCurrentFitSpecialist ? user?.id : '',
        greets: 0,
        sitDowns: 0,
        testRides: 0,
        writeUps: 0
      });
    } catch (err) {
      setAlertModal({ isOpen: true, title: 'Error', message: err.message });
    }
  };

  const handleEdit = (entry) => {
    const employeeId = entry.userId
      ? entry.userId
      : (entry.specialistValue ? `named:${entry.specialistValue}` : '');
    setFormData({
      date: entry.date.split('T')[0],
      dealershipId: entry.dealershipId,
      employeeId,
      greets: entry.greets,
      sitDowns: entry.sitDowns,
      testRides: entry.testRides,
      writeUps: entry.writeUps
    });
    setEditingId(entry.id);
    setShowForm(true);
    fetchEmployees(entry.dealershipId);
    fetchNamedFitSpecialists(entry.dealershipId);
  };

  const handleDelete = (id) => {
    setConfirmModal({
      isOpen: true,
      entryId: id,
      title: 'Delete Entry',
      message: 'Are you sure you want to delete this entry? This action cannot be undone.',
      confirmText: 'Delete',
      confirmStyle: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const response = await fetch(`${API_URL}/api/daily-logs/${id}`, {
            method: 'DELETE',
            headers,
            credentials: 'include'
          });
          if (!response.ok) {
            let message = 'Failed to delete entry';
            try {
              const err = await response.json();
              if (err?.error) message = err.error;
            } catch {}
            throw new Error(message);
          }
          await fetchEntries(formData.dealershipId || selectedDealership || singleDealership);
        } catch (err) {
          setError(err.message);
        } finally {
          setConfirmModal(c => ({ ...c, isOpen: false, entryId: null, onConfirm: null }));
        }
      }
    });
  };

  // confirmDelete is now handled inline in handleDelete/onConfirm

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    const activeDealership = selectedDealership || singleDealership;
    setFormData({
      date: getLocalDateString(),
      dealershipId: activeDealership || '',
      employeeId: isCurrentFitSpecialist ? user?.id : '',
      greets: 0,
      sitDowns: 0,
      testRides: 0,
      writeUps: 0
    });
  };

  const handleDuplicateAction = async (action) => {
    const existingEntry = duplicateModal.entry;
    if (!existingEntry) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const dateString = formData.date;

      if (action === 'replace') {
        // Delete the old entry first, then continue with new save
        const deleteResponse = await fetch(
          `${API_URL}/api/daily-logs/${existingEntry.id}`,
          {
            method: 'DELETE',
            headers,
            credentials: 'include'
          }
        );
        if (!deleteResponse.ok) {
          throw new Error('Failed to replace entry');
        }
        
        setDuplicateModal({ show: false, entry: null, resolve: null });
        
        // Now save the new entry (use the dealership from existing entry since we're replacing it)
        const dealershipId = formData.dealershipId || existingEntry.dealershipId;
        const url = `${API_URL}/api/dealerships/${dealershipId}/daily-logs`;
        const saveResponse = await fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            date: dateString,
            ...buildSpecialistBody(formData.employeeId),
            greets: parseInt(formData.greets) || 0,
            sitDowns: parseInt(formData.sitDowns) || 0,
            testRides: parseInt(formData.testRides) || 0,
            writeUps: parseInt(formData.writeUps) || 0
          })
        });

        if (!saveResponse.ok) {
          const error = await saveResponse.json();
          throw new Error(error.error || 'Failed to save entry');
        }

        const activeDealership = selectedDealership || singleDealership;
        await fetchEntries(activeDealership);
        setShowForm(false);
        setEditingId(null);
        setFormData({
          date: getLocalDateString(),
          dealershipId: activeDealership || '',
          employeeId: isCurrentFitSpecialist ? user?.id : '',
          greets: 0,
          sitDowns: 0,
          testRides: 0,
          writeUps: 0
        });
      } else if (action === 'combine') {
        // Combine: add new values to existing entry
        const combinedData = {
          date: dateString,
          ...buildSpecialistBody(formData.employeeId),
          greets: parseInt(formData.greets) + parseInt(existingEntry.greets) || 0,
          sitDowns: parseInt(formData.sitDowns) + parseInt(existingEntry.sitDowns) || 0,
          testRides: parseInt(formData.testRides) + parseInt(existingEntry.testRides) || 0,
          writeUps: parseInt(formData.writeUps) + parseInt(existingEntry.writeUps) || 0
        };

        const updateResponse = await fetch(
          `${API_URL}/api/daily-logs/${existingEntry.id}`,
          {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: JSON.stringify(combinedData)
          }
        );

        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          throw new Error(error.error || 'Failed to combine entries');
        }

        const activeDealership = selectedDealership || singleDealership;
        await fetchEntries(activeDealership);
        setShowForm(false);
        setEditingId(null);
        setFormData({
          date: getLocalDateString(),
          dealershipId: activeDealership || '',
          employeeId: isCurrentFitSpecialist ? user?.id : '',
          greets: 0,
          sitDowns: 0,
          testRides: 0,
          writeUps: 0
        });
        setDuplicateModal({ show: false, entry: null, resolve: null });
      } else {
        // Cancel
        setDuplicateModal({ show: false, entry: null, resolve: null });
      }
    } catch (err) {
      setAlertModal({ isOpen: true, title: 'Error', message: err.message });
      setDuplicateModal({ show: false, entry: null, resolve: null });
    }
  };

  const handleDealershipChange = (dealerId) => {
    setSelectedDealership(dealerId);
    setViewingAllDealerships(dealerId === 'ALL');
    
    if (dealerId === 'ALL') {
      fetchEntries('ALL');
      setEmployees(allDealershipEmployees);
    } else {
      setFormData(prev => ({ ...prev, dealershipId: dealerId, employeeId: '' }));
      setEmployees([]);
      if (dealerId) {
        fetchEmployees(dealerId);
        fetchEntries(dealerId);
        fetchNamedFitSpecialists(dealerId);
      }
    }
  };

  const downloadCSVTemplate = () => {
    const headers = ['Date', 'Dealership Name', 'Employee Email', 'Greets', 'Sit Downs', 'Test Rides', 'Write Ups'];
    const sampleRow = ['2025-10-10', 'Coastal Harley-Davidson', 'employee@example.com', '10', '5', '3', '2'];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'fi_input_template.csv';
    link.click();
  };

  const parseCsvFile = async (file) => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain headers and at least one data row');
    }

    const csvHeaders = lines[0].split(',').map(h => h.trim());
    const dataLines = lines.slice(1);
    const requiredHeaders = ['Date', 'Dealership Name', 'Employee Email', 'Greets', 'Sit Downs', 'Test Rides', 'Write Ups'];
    const missingHeaders = requiredHeaders.filter(h => !csvHeaders.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const rows = dataLines.map((line, index) => {
      const values = line.split(',').map(v => v.trim());
      const row = {};
      csvHeaders.forEach((header, i) => {
        row[header] = values[i] || '';
      });

      if (!row['Date'] || !row['Employee Email'] || !row['Dealership Name']) {
        throw new Error(`Row ${index + 2}: Date, Dealership Name, and Employee Email are required`);
      }

      return {
        date: row['Date'],
        dealershipId: row['Dealership Name'],
        employeeEmail: row['Employee Email'],
        greets: row['Greets'],
        sitDowns: row['Sit Downs'],
        testRides: row['Test Rides'],
        writeUps: row['Write Ups']
      };
    });

    return rows;
  };

  const validateBulkEntries = async (entriesToValidate) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/daily-logs/bulk-import/validate`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entries: entriesToValidate })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to validate import');
    }

    return response.json();
  };

  const executeBulkImport = async (entriesToImport) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/daily-logs/bulk-import/execute`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entries: entriesToImport, createMissingEmployees: bulkCreateMissing })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import entries');
    }

    return response.json();
  };

  const handleBulkFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setBulkImportError(null);
      setBulkImportSuccess(null);
      setBulkFileName(file.name);

      const parsed = await parseCsvFile(file);
      setBulkEntries(parsed);

      const validation = await validateBulkEntries(parsed);
      setBulkValidation(validation);
      setBulkStep('review');
    } catch (err) {
      setBulkImportError(err.message);
      setBulkValidation(null);
    }

    event.target.value = '';
  };

  const handleBulkExecute = async () => {
    try {
      setBulkImportError(null);
      setBulkImportSuccess(null);
      setBulkStep('importing');

      const entriesToImport = bulkValidation?.normalizedEntries?.length ? bulkValidation.normalizedEntries : bulkEntries;
      const result = await executeBulkImport(entriesToImport);

      setBulkImportSuccess(`Imported ${result.imported} entries${result.createdUsers ? `, created ${result.createdUsers} users` : ''}`);
      setBulkStep('done');

      // Refresh entries
      const currentDealership = selectedDealership || singleDealership;
      await fetchEntries(currentDealership || 'ALL');
    } catch (err) {
      setBulkImportError(err.message);
      setBulkStep('review');
    }
  };

  // Filter and sort entries
  const filteredEntries = entries.filter(entry => {
    // Dealership filter
    if (selectedDealerships.length > 0 && !selectedDealerships.includes(entry.dealershipId)) {
      return false;
    }
    
    // Employee/specialist filter
    if (selectedEmployees.length > 0) {
      const userIds = selectedEmployees.filter(id => !id.startsWith('named:'));
      const namedValues = selectedEmployees.filter(id => id.startsWith('named:')).map(id => id.slice(6));
      const matchesUser = entry.userId && userIds.includes(entry.userId);
      const matchesNamed = !entry.userId && namedValues.includes(entry.specialistValue);
      if (!matchesUser && !matchesNamed) return false;
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const userName = (entry.user?.name || entry.specialistName || '').toLowerCase();
      const dealershipName = entry.dealership?.name?.toLowerCase() || '';
      if (!userName.includes(search) && !dealershipName.includes(search)) {
        return false;
      }
    }
    
    return true;
  });
  
  // Sort entries
  const sortedEntries = [...filteredEntries].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'date':
        aValue = new Date(a.date);
        bValue = new Date(b.date);
        break;
      case 'employee':
        aValue = a.user?.name || a.specialistName || '';
        bValue = b.user?.name || b.specialistName || '';
        break;
      case 'dealership':
        aValue = a.dealership?.name || '';
        bValue = b.dealership?.name || '';
        break;
      default:
        aValue = a[sortField] || 0;
        bValue = b[sortField] || 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Calculate active filters
  const activeFilters = [];
  selectedDealerships.forEach(id => {
    const dealership = dealerships.find(d => d.id === id);
    if (dealership) {
      activeFilters.push({ type: 'dealership', value: id, label: dealership.name });
    }
  });
  selectedEmployees.forEach(id => {
    if (id.startsWith('named:')) {
      const namedValue = id.slice(6);
      const ns = namedFitSpecialists.find(n => n.value === namedValue);
      if (ns) activeFilters.push({ type: 'employee', value: id, label: ns.label });
    } else {
      const employee = employees.find(e => e.id === id);
      if (employee) activeFilters.push({ type: 'employee', value: id, label: employee.name });
    }
  });
  
  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(prev => prev.filter(d => d !== value));
    } else if (type === 'employee') {
      setSelectedEmployees(prev => prev.filter(e => e !== value));
    }
  };
  
  const toggleAllDealerships = () => {
    if (selectedDealerships.length === 0) {
      setSelectedDealerships(dealerships.map(d => d.id));
    } else {
      setSelectedDealerships([]);
    }
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) return <LoadingState message="Loading daily logs..." fullscreen />;
  if (error) return <div className="daily-logger-error">Error: {error}</div>;

  const activeDealership = selectedDealership || singleDealership;
  const dealershipName = viewingAllDealerships
    ? 'All Dealerships'
    : isOwner 
      ? dealerships.find(d => d.id === activeDealership)?.name 
      : (user?.dealershipName || '');

  return (
    <div className="daily-logger-container">
      {/* Branded Confirm Modal (always rendered at top level) */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(c => ({ ...c, isOpen: false, entryId: null, onConfirm: null }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
      />
      {/* Branded Alert Modal (errors and validation) */}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
      <div className="daily-logger-header">
        <div>
          <h1>Daily Fit Specialist 10:4 Sales Process</h1>
          <p>Track your daily greets, sit downs, test rides, and write ups</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-bar-left">
          <button 
            className={`filter-button ${activeFilters.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {activeFilters.length > 0 && ` (${activeFilters.length})`}
          </button>

          {activeFilters.length > 0 && (
            <div className="active-filters">
              {activeFilters.map(filter => (
                <div key={`${filter.type}-${filter.value}`} className="active-filter-chip">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bulk Import Button */}
        {(user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.role === 'ADMIN' || managedOwner) && (
          <div className="bulk-import-container">
            <button 
              className="bulk-import-button"
              onClick={() => {
                setShowBulkModal(true);
                setBulkStep('upload');
                setBulkValidation(null);
                setBulkImportError(null);
                setBulkImportSuccess(null);
                setBulkEntries([]);
                setBulkFileName('');
                setBulkCreateMissing(true);
              }}
              title="Bulk Import"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                <path d="M19.4 15a1.6 1.6 0 0 0 .33 1.8l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.8-.33 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.09a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .33-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.09a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.33-1.8l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.8.33h.09a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.09a1.6 1.6 0 0 0 1 1.5h.09a1.6 1.6 0 0 0 1.8-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.33 1.8v.09a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1Z"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              {/* Dealership Filter */}
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={toggleAllDealerships} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>

              {/* Fit Specialist Filter */}
              {(fitSpecialists.length > 0 || namedFitSpecialists.length > 0) && (
                <div className="filter-section">
                  <div className="filter-section-header">
                    <h4>Fit Specialists</h4>
                    <button
                      className="btn-select-all"
                      onClick={() => {
                        const allIds = [
                          ...fitSpecialists.map(e => e.id),
                          ...namedFitSpecialists.map(ns => `named:${ns.value}`)
                        ];
                        if (selectedEmployees.length === allIds.length) {
                          setSelectedEmployees([]);
                        } else {
                          setSelectedEmployees(allIds);
                        }
                      }}
                    >
                      {selectedEmployees.length === 0 ? 'Select All' : 'Clear All'}
                    </button>
                  </div>
                  {fitSpecialists.map(employee => (
                    <label key={employee.id} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployees([...selectedEmployees, employee.id]);
                          } else {
                            setSelectedEmployees(selectedEmployees.filter(id => id !== employee.id));
                          }
                        }}
                      />
                      <span>{employee.name}</span>
                    </label>
                  ))}
                  {namedFitSpecialists.map(ns => {
                    const prefixedId = `named:${ns.value}`;
                    return (
                      <label key={prefixedId} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(prefixedId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees([...selectedEmployees, prefixedId]);
                            } else {
                              setSelectedEmployees(selectedEmployees.filter(id => id !== prefixedId));
                            }
                          }}
                        />
                        <span>{ns.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="daily-logger-form-container">
          <form onSubmit={handleSubmit} className="daily-logger-form">
            <h2>{editingId ? 'Edit Entry' : 'New Entry'}</h2>

            <div className="form-group">
              <label>Date</label>
              <DatePicker
                asString
                hideLabel
                className="dp-form"
                selectedDate={formData.date}
                onDateChange={(val) => setFormData({ ...formData, date: val })}
              />
            </div>

            {/* Dealership field - shown as readonly for single dealership, dropdown for multiple */}
            {isOwner && hasMultipleDealerships ? (
              <div className="form-group">
                <label>Dealership </label>
                <select
                  value={formData.dealershipId}
                  onChange={(e) => {
                    const dealerId = e.target.value;
                    setFormData({ ...formData, dealershipId: dealerId, employeeId: '' });
                    if (dealerId) fetchNamedFitSpecialists(dealerId);
                  }}
                  required
                >
                  <option value="">Select a Dealership</option>
                  {dealerships.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label>Dealership</label>
                <div className="form-readonly-text">{dealershipName}</div>
              </div>
            )}

            {/* Employee field - shown as readonly for fit specialists, dropdown for managers */}
            {isCurrentFitSpecialist ? (
              <div className="form-group">
                <label>Employee</label>
                <div className="form-readonly-text">{user?.name}</div>
              </div>
            ) : (
              <div className="form-group">
                <label>Fit Specialist </label>
                <select
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  required
                >
                  <option value="">Select a Fit Specialist</option>
                  {formFitSpecialists.length > 0 && (
                    <optgroup label="Fit Specialists">
                      {formFitSpecialists.map(fs => (
                        <option key={fs.id} value={fs.id}>{fs.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {namedFitSpecialists.filter(ns => !formFitSpecialists.some(fs => fs.name === ns.label)).length > 0 && (
                    <optgroup label="Named Specialists">
                      {namedFitSpecialists
                        .filter(ns => !formFitSpecialists.some(fs => fs.name === ns.label))
                        .map(ns => (
                          <option key={ns.value} value={`named:${ns.value}`}>{ns.label}</option>
                        ))}
                    </optgroup>
                  )}
                  {formFitSpecialists.length === 0 && namedFitSpecialists.length === 0 && (
                    <option value="" disabled>No fit specialists configured</option>
                  )}
                </select>
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label>Greets</label>
                <input
                  type="number"
                  min="0"
                  value={formData.greets}
                  onChange={(e) => setFormData({ ...formData, greets: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Sit Downs</label>
                <input
                  type="number"
                  min="0"
                  value={formData.sitDowns}
                  onChange={(e) => setFormData({ ...formData, sitDowns: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Test Rides</label>
                <input
                  type="number"
                  min="0"
                  value={formData.testRides}
                  onChange={(e) => setFormData({ ...formData, testRides: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Write Ups</label>
                <input
                  type="number"
                  min="0"
                  value={formData.writeUps}
                  onChange={(e) => setFormData({ ...formData, writeUps: e.target.value })}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-cancel">
                Cancel
              </button>
              <button type="submit" className="btn-save">
                {editingId ? 'Update Entry' : 'Save Entry'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeDealership && (
        <div className="daily-logger-table">
          <div className="activities-controls">
            <div className="search-controls">
              <input
                type="text"
                placeholder="Search employee or dealership..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {!showForm && (
              <button className="btn-add-entry" onClick={() => setShowForm(true)}>
                + New Entry
              </button>
            )}
          </div>

          {sortedEntries.length === 0 ? (
            <p className="no-entries">
              {entries.length === 0 ? 'No entries yet. Create one to get started!' : 'No entries match the current filters.'}
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('date')}>
                    Date
                    {sortField === 'date' && (
                      <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('employee')}>
                    Employee
                    {sortField === 'employee' && (
                      <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  {isOwner && (
                    <th className="sortable" onClick={() => handleSort('dealership')}>
                      Dealership
                      {sortField === 'dealership' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  )}
                  <th className="sortable" onClick={() => handleSort('greets')}>
                    Greets
                    {sortField === 'greets' && (
                      <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('sitDowns')}>
                    Sit Downs
                    {sortField === 'sitDowns' && (
                      <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('testRides')}>
                    Test Rides
                    {sortField === 'testRides' && (
                      <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('writeUps')}>
                    Write Ups
                    {sortField === 'writeUps' && (
                      <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry) => {
                  return (
                    <tr key={entry.id}>
                      <td>{entry.date.split('T')[0]}</td>
                      <td>{entry.user?.name || entry.specialistName || 'Unknown'}</td>
                      {isOwner && <td>{entry.dealership?.name || dealershipName}</td>}
                      <td>{entry.greets}</td>
                      <td>{entry.sitDowns}</td>
                      <td>{entry.testRides}</td>
                      <td>{entry.writeUps}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(entry)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(entry.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showBulkModal && (
        <div className="bulk-modal-overlay">
          <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bulk-modal-header">
              <div>
                <h3>Bulk Import (CSV)</h3>
                <p>Steps: Upload → Review → Import</p>
              </div>
              <button className="bulk-modal-close" onClick={() => setShowBulkModal(false)} disabled={bulkStep === 'importing'}>x</button>
            </div>

            <div className="bulk-modal-body">
              {bulkStep === 'upload' && (
                <div className="bulk-step">
                  <p className="bulk-helper">Download the template, add rows with Date, Dealership Name, and Employee Email (required), then upload to validate. If you don't have the employee email for your imported data, you can enter your email, but the employee insights will not be available for the data.</p>
                  <div className="bulk-actions-row">
                    <button className="bulk-secondary" onClick={downloadCSVTemplate}>Download Template</button>
                    <label className="bulk-primary">
                      Attach CSV
                      <input type="file" accept=".csv" onChange={handleBulkFileSelect} style={{ display: 'none' }} />
                    </label>
                  </div>
                  {bulkFileName && <p className="bulk-file-name">Attached: {bulkFileName}</p>}
                  {bulkImportError && <div className="bulk-import-error">{bulkImportError}</div>}
                </div>
              )}

              {bulkStep === 'review' && (
                <div className="bulk-step">
                  <h4>Review</h4>
                  {bulkValidation?.errors?.length ? (
                    <div className="bulk-import-error">
                      <p>Errors found:</p>
                      <ul>
                        {bulkValidation.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="bulk-import-success">Validation passed. No row errors.</div>
                  )}

                  {bulkValidation?.missingEmployees?.length > 0 && (
                    <div className="bulk-missing">
                      <div className="bulk-missing-header">
                        <span>{bulkValidation.missingEmployees.length} employees not found in listed dealerships.</span>
                        <label className="bulk-checkbox">
                          <input
                            type="checkbox"
                            checked={bulkCreateMissing}
                            onChange={(e) => setBulkCreateMissing(e.target.checked)}
                          />
                          Create missing employees and add to dealership
                        </label>
                      </div>
                      <div className="bulk-missing-list">
                        {bulkValidation.missingEmployees.map((m, idx) => (
                          <div key={idx} className="bulk-missing-row">
                            <span className="bulk-pill">{m.email}</span>
                            <span className="bulk-pill subtle">{m.dealershipName || m.dealershipId}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkImportError && <div className="bulk-import-error">{bulkImportError}</div>}
                  {bulkImportSuccess && <div className="bulk-import-success">{bulkImportSuccess}</div>}
                </div>
              )}

              {bulkStep === 'importing' && (
                <div className="bulk-step">
                  <p>Importing... please wait.</p>
                </div>
              )}

              {bulkStep === 'done' && (
                <div className="bulk-step">
                  <div className="bulk-import-success">Import complete. {bulkImportSuccess}</div>
                  {bulkImportError && <div className="bulk-import-error">{bulkImportError}</div>}
                </div>
              )}
            </div>

            <div className="bulk-modal-footer">
              {bulkStep === 'upload' && (
                <button className="bulk-primary" onClick={downloadCSVTemplate}>Download Template</button>
              )}

              {bulkStep === 'review' && (
                <div className="bulk-footer-actions">
                  <button className="bulk-secondary" onClick={() => setBulkStep('upload')}>Back</button>
                  <button
                    className="bulk-primary"
                    onClick={handleBulkExecute}
                    disabled={!!(bulkValidation?.errors?.length)}
                  >
                    Next: Import
                  </button>
                </div>
              )}

              {bulkStep === 'importing' && (
                <button className="bulk-primary" disabled>Importing...</button>
              )}

              {bulkStep === 'done' && (
                <button className="bulk-primary" onClick={() => setShowBulkModal(false)}>Done</button>
              )}
            </div>
          </div>
        </div>
      )}

      {!activeDealership && (
        <div className="daily-logger-error">
          Please select a dealership to view and manage daily logs.
        </div>
      )}

      {/* Duplicate Entry Modal */}
      {duplicateModal.show && duplicateModal.entry && (
        <>
          <div className="modal-overlay" onClick={() => handleDuplicateAction('cancel')} />
          <div className="duplicate-modal">
            <div className="modal-content">
              <h2>Entry Already Exists</h2>
              <p className="modal-message">
                An entry already exists for <strong>{duplicateModal.entry.user?.name || duplicateModal.entry.specialistName}</strong> at <strong>{duplicateModal.entry.dealership?.name}</strong> on <strong>{duplicateModal.entry.date.split('T')[0]}</strong>.
              </p>
              <p className="modal-subtext">
                What would you like to do?
              </p>
              <div className="modal-actions">
                <button
                  className="modal-btn modal-btn-replace"
                  onClick={() => handleDuplicateAction('replace')}
                >
                  Replace Entry
                </button>
                <button
                  className="modal-btn modal-btn-combine"
                  onClick={() => handleDuplicateAction('combine')}
                >
                  Combine Entries
                </button>
                <button
                  className="modal-btn modal-btn-cancel"
                  onClick={() => handleDuplicateAction('cancel')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
